import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, storage } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { updateUserProfile, setupProgramByPersona, getPersonasConfig, getProgramsCatalog } from '../utils/database';
import { getClubBySlug, createClubMembership } from '../utils/clubs';
import { getCoachBySlug, createCoachMembership } from '../utils/coaches';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import '../styles/Onboarding.css';
import Badge from '../assets/images/Begin-Badge.png';
import Logo from '../assets/logo.svg';
import InfoIcon from '../assets/icons/info-circle.svg';
import quotesData from '../data/quotes.json';
import BuildingPlanScreen from '../components/BuildingPlanScreen';
import ProfilePictureCropper from '../components/ProfilePictureCropper';

function OnboardingFlow() {
  const navigate = useNavigate();
  const location = useLocation();
  const clubSlug = location.state?.clubSlug || new URLSearchParams(location.search).get('club');
  const coachSlug = location.state?.coachSlug || new URLSearchParams(location.search).get('coach');
  const redirectAfter = location.state?.redirect || null;
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Personas/programs config from DB
  const [personasData, setPersonasData] = useState(null);
  // Live program catalog from Firestore programs collection (name/description overrides)
  const [programsCatalog, setProgramsCatalog] = useState({});

  // Random quote
  const [randomQuote] = useState(() => {
    const randomIndex = Math.floor(Math.random() * quotesData.length);
    return quotesData[randomIndex];
  });

  // Step 1: Profile data
  const [birthdate, setBirthdate] = useState('');

  // Step 2: Persona + Program selection (pre-select athlete + volleyball when joining via club/coach)
  const hasTeamContext = !!(clubSlug || coachSlug);
  const [selectedPersonaId, setSelectedPersonaId] = useState(hasTeamContext ? 'athlete' : '');
  
  // Calculate age from birthdate
  const calculateAge = (birthdateStr) => {
    if (!birthdateStr) return null;
    const today = new Date();
    const birth = new Date(birthdateStr);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };
  const [weight, setWeight] = useState('');
  const [weightUnit, setWeightUnit] = useState('lb');
  const [goalWeight, setGoalWeight] = useState('');
  const [goalWeightUnit, setGoalWeightUnit] = useState('lb');
  const [gender, setGender] = useState('');
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  
  // Tooltip state
  const [activeTooltip, setActiveTooltip] = useState(null);

  // Step 2 continued: Program selection (within the selected persona)
  const [selectedProgramId, setSelectedProgramId] = useState(hasTeamContext ? 'court_volleyball' : '');
  const [showingPrograms, setShowingPrograms] = useState(hasTeamContext);

  // Activities data - Keep for dashboard/calendar use
  // eslint-disable-next-line no-unused-vars
  const [selectedActivities, setSelectedActivities] = useState({ 'Strength Training': 3 });
  // eslint-disable-next-line no-unused-vars
  const [customActivityName, setCustomActivityName] = useState('');

  // Step 3: Waiver acceptance
  const [waiverAccepted, setWaiverAccepted] = useState(false);

  const totalSteps = 3;

  // Building screen state
  const [showBuildingScreen, setShowBuildingScreen] = useState(false);
  const [buildingComplete, setBuildingComplete] = useState(false);

  // Get the selected persona object and its programs, overlaying live names from DB
  const personas = personasData?.personas || [];
  const selectedPersona = personas.find(p => p.id === selectedPersonaId);
  const availablePrograms = (selectedPersona?.programs || []).map(prog => {
    const live = prog.programId ? programsCatalog[prog.programId] : null;
    return live ? { ...prog, name: live.name, description: live.description || prog.description } : prog;
  });
  const selectedProgram = availablePrograms.find(p => p.id === selectedProgramId);

  // Clear selected program if persona changes
  useEffect(() => {
    if (selectedProgramId && selectedPersona && !selectedPersona.programs.find(p => p.id === selectedProgramId)) {
      setSelectedProgramId('');
      console.log('[OnboardingFlow] Cleared selected program due to persona change');
    }
  }, [selectedPersonaId, selectedProgramId, selectedPersona]);

  // Debug logging
  useEffect(() => {
    console.log('[OnboardingFlow] Mounted, current step:', currentStep);
    console.log('[OnboardingFlow] User:', auth.currentUser?.uid);
  }, [currentStep]);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Check if user is authenticated - wait for Firebase to restore session
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        console.log('[OnboardingFlow] No user, redirecting to signin');
        navigate('/signin');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Load personas config + live program catalog from Firestore
  useEffect(() => {
    getPersonasConfig().then((data) => {
      if (data?.personas) {
        setPersonasData(data);
        console.log('[OnboardingFlow] Loaded personas config from DB');
      }
    }).catch((err) => {
      console.error('[OnboardingFlow] Failed to load personas config:', err);
    });

    getProgramsCatalog().then((catalog) => {
      setProgramsCatalog(catalog);
    }).catch(() => {});
  }, []);

  // Validation for each step
  const isStep1Valid = birthdate && weight && goalWeight && gender;
  const isStep2Valid = !!selectedPersonaId && !!selectedProgramId;
  // Step 3 (Waiver) requires explicit acceptance
  const isStep3Valid = waiverAccepted;

  const isCurrentStepValid = () => {
    switch (currentStep) {
      case 1: return isStep1Valid;
      case 2: return isStep2Valid;
      case 3: return isStep3Valid;
      default: return false;
    }
  };

  // Navigation
  const goBack = () => {
    if (currentStep === 2 && showingPrograms) {
      setShowingPrograms(false);
      setSelectedProgramId('');
      return;
    }
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setShowingPrograms(false);
      console.log('[OnboardingFlow] Going back to step:', currentStep - 1);
    }
  };

  const goNext = async () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      console.log('[OnboardingFlow] Going to step:', currentStep + 1);
    } else {
      // Complete onboarding
      await completeOnboarding();
    }
  };

  const completeOnboarding = async () => {
    setLoading(true);
    setError('');
    console.log('[OnboardingFlow] Completing onboarding...');

    try {
      const userId = auth.currentUser?.uid;
      if (!userId) throw new Error('No user logged in');

      // Upload profile picture to storage if exists
      let profilePictureUrl = null;
      if (profilePicture) {
        console.log('[OnboardingFlow] Uploading profile picture...');
        const fileExtension = profilePicture.name.split('.').pop();
        const storageRef = ref(storage, `users/${userId}/profile-picture.${fileExtension}`);
        await uploadBytes(storageRef, profilePicture);
        profilePictureUrl = await getDownloadURL(storageRef);
        console.log('[OnboardingFlow] Profile picture uploaded, URL:', profilePictureUrl);
      }

      // Convert selectedActivities to array format (for dashboard/calendar use)
      const activitiesArray = Object.entries(selectedActivities).map(([name, daysPerWeek]) => ({
        activityId: name.toLowerCase().replace(/\s+/g, '_'),
        name,
        daysPerWeek
      }));

      // Get the program data to check if it has an actual programId
      const programData = selectedProgram;
      const hasProgramToSetup = programData?.programId != null && programData?.status === 'available';

      await updateUserProfile(userId, {
        // Step 1
        birthdate: birthdate,
        age: calculateAge(birthdate),
        weight: parseFloat(weight),
        weightUnit,
        goalWeight: parseFloat(goalWeight),
        goalWeightUnit,
        gender,
        profilePictureUrl,
        // Persona & Program
        personaId: selectedPersonaId,
        selectedProgramId: selectedProgramId,
        programId: programData?.programId || null,
        // Activities (for dashboard/calendar, not a step in onboarding)
        additionalActivities: activitiesArray,
        // Step 3 (Waiver)
        liabilityWaiverAccepted: true,
        liabilityWaiverAcceptedAt: new Date(),
        liabilityWaiverVersion: '2025-01-01',
        // System
        onboardingComplete: true
      });

      console.log('[OnboardingFlow] Profile saved');

      // If joining via club/coach link, create membership
      if (clubSlug) {
        try {
          const club = await getClubBySlug(clubSlug);
          if (club) {
            const year = new Date().getFullYear();
            await createClubMembership(userId, club.id, year);
            console.log('[OnboardingFlow] Club membership created for', club.name);
          }
        } catch (clubErr) {
          console.error('[OnboardingFlow] Club membership error:', clubErr);
        }
      } else if (coachSlug) {
        try {
          const coach = await getCoachBySlug(coachSlug);
          if (coach) {
            await createCoachMembership(userId, coach.id);
            console.log('[OnboardingFlow] Coach membership created for', coach.name);
          }
        } catch (coachErr) {
          console.error('[OnboardingFlow] Coach membership error:', coachErr);
        }
      }

      // If program has an actual programId (is available), show building screen and set it up
      if (hasProgramToSetup) {
        console.log('[OnboardingFlow] Setting up program:', programData.programId, 'for persona:', selectedPersonaId);
        setShowBuildingScreen(true);
        
        // Start building program in background
        try {
          const result = await setupProgramByPersona(userId, programData.programId);
          console.log('[OnboardingFlow] Program setup result:', result);
          setBuildingComplete(true);
        } catch (programError) {
          console.error('[OnboardingFlow] Program setup error:', programError);
          // Still mark as complete - they can import manually later
          setBuildingComplete(true);
        }
      } else {
        // No program to set up (coming soon), go to redirect target or dashboard
        const destination = redirectAfter || `/${userId}/dashboard`;
        console.log('[OnboardingFlow] Program coming soon, redirecting to:', destination);
        navigate(destination);
      }
    } catch (err) {
      console.error('[OnboardingFlow] Error:', err);
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  // Handle building screen completion (animation finished)
  const handleBuildingComplete = () => {
    const userId = auth.currentUser?.uid;
    const destination = redirectAfter || `/${userId}/dashboard`;
    console.log('[OnboardingFlow] Building animation complete, redirecting to:', destination);
    navigate(destination);
  };

  // Progress bar click handler
  const goToStep = (step) => {
    if (step < currentStep) {
      setCurrentStep(step);
      console.log('[OnboardingFlow] Jumped to step:', step);
    }
  };

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activeTooltip && !e.target.closest('.onboarding-info-icon-wrapper')) {
        setActiveTooltip(null);
      }
    };
    if (activeTooltip) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeTooltip]);

  // Get program name for building screen
  const getProgramDisplayName = () => {
    if (selectedProgram) {
      return `${selectedPersona?.name} - ${selectedProgram.name}`;
    }
    return selectedPersona?.name || 'Training';
  };

  // Show building screen when program is being set up
  if (showBuildingScreen) {
    return (
      <BuildingPlanScreen 
        personaName={getProgramDisplayName()} 
        onComplete={buildingComplete ? handleBuildingComplete : undefined}
      />
    );
  }

  return (
    <div className="onboarding-page">
      {/* Background effects */}
      <div className="onboarding-bg-glow onboarding-bg-glow-1" />
      <div className="onboarding-bg-glow onboarding-bg-glow-2" />
      <div className="onboarding-bg-overlay" />

      {/* Top row: branding + progress side by side on desktop */}
      <div className="onboarding-top-row">
        <div className="onboarding-header">
          <div className="onboarding-branding">
            {profilePicturePreview ? (
              <img src={profilePicturePreview} alt="Profile" className="onboarding-profile-picture" />
            ) : (
              <img src={Badge} alt="" className="onboarding-badge" />
            )}
            <div className="onboarding-brand-text">
              <img src={Logo} alt="Begin Strength" className="onboarding-logo" />
              <p className="onboarding-quote">"{randomQuote.quote}"</p>
              <p className="onboarding-quote-author">— {randomQuote.author}</p>
            </div>
          </div>
          {currentStep > 1 && (
            <button onClick={goBack} className="onboarding-back">
              ← Back
            </button>
          )}
        </div>

        {/* Progress bar */}
        <div className="onboarding-progress">
          {[1, 2, 3].map((step) => (
            <div
              key={step}
              className={`onboarding-progress-step ${step === currentStep ? 'active' : ''} ${step < currentStep ? 'completed' : ''}`}
              onClick={() => goToStep(step)}
            >
              <div className="onboarding-progress-dot">
                {step < currentStep ? '✓' : step}
              </div>
              <span className="onboarding-progress-label">
                {step === 1 && 'Profile'}
                {step === 2 && 'Program'}
                {step === 3 && 'Waiver'}
              </span>
            </div>
          ))}
          <div className="onboarding-progress-line" />
        </div>
      </div>

      {/* Step 2: Path & Program Selection - Two column layout */}
      {currentStep === 2 && (
        <div className="onboarding-persona-layout">
          {/* Left: Path + Program Selection with slide transition */}
          <div className="onboarding-card onboarding-persona-select">
            <h2 className="onboarding-card-title">What brings you to Begin Strength?</h2>

            <div className="onboarding-slide-container">
              <div className={`onboarding-slide-wrapper ${showingPrograms ? 'showing-programs' : ''}`}>
                {/* Panel 1: Persona (Path) cards */}
                <div className="onboarding-slide-panel">
                  <div className="onboarding-path-cards">
                    {personas.map((persona) => (
                      <button
                        key={persona.id}
                        type="button"
                        className={`c-ripple onboarding-path-card ${selectedPersonaId === persona.id ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedPersonaId(persona.id);
                          setSelectedProgramId('');
                          setShowingPrograms(true);
                        }}
                      >
                        <div className="onboarding-path-card-name">{persona.name}</div>
                        <div className="onboarding-path-card-desc">{persona.description}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Panel 2: Programs for selected persona */}
                <div className="onboarding-slide-panel">
                  {selectedPersona && (
                    <>
                      <button
                        type="button"
                        className="c-ripple onboarding-breadcrumb"
                        onClick={() => {
                          setShowingPrograms(false);
                          setSelectedProgramId('');
                        }}
                      >
                        ← Paths
                      </button>
                      <h3 className="onboarding-programs-heading">{selectedPersona.name} Programs</h3>
                      <div className="onboarding-persona-options">
                        {availablePrograms.map((program) => (
                          <button
                            key={program.id}
                            className={`c-ripple onboarding-persona-option ${selectedProgramId === program.id ? 'selected' : ''} ${program.status === 'coming_soon' ? 'coming-soon' : ''}`}
                            onClick={() => {
                              if (program.status === 'coming_soon') return;
                              setSelectedProgramId(program.id);
                              console.log('[OnboardingFlow] Selected program:', program.id);
                            }}
                            disabled={program.status === 'coming_soon'}
                          >
                            <span className="onboarding-program-name">{program.name}</span>
                            {program.status === 'coming_soon' && (
                              <span className="onboarding-program-badge">Coming Soon</span>
                            )}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Error message */}
            {error && <p className="onboarding-error">{error}</p>}

            {/* Next button */}
            <button
              onClick={goNext}
              className="c-ripple onboarding-submit"
              disabled={!isCurrentStepValid() || loading}
            >
              {loading ? 'Please wait...' : 'Next'}
            </button>
          </div>

          {/* Right: Description */}
          <div className="onboarding-card onboarding-persona-detail">
            {selectedProgram ? (
              <>
                <h2 className="onboarding-card-title">{selectedProgram.name}</h2>
                <div className="onboarding-persona-content">
                  <p className="onboarding-persona-description">{selectedProgram.description}</p>
                  
                  <p className="onboarding-persona-mindset">"{selectedPersona.mindset}"</p>
                  
                  <h3>Who This Fits:</h3>
                  <ul className="onboarding-persona-list">
                    {selectedPersona.whoFits.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>

                  <h3>Periodization Structure:</h3>
                  <p><strong>Macrocycle:</strong> {selectedPersona.periodization.macrocycle}</p>
                  <p><strong>Mesocycles (12 weeks each):</strong></p>
                  <ul className="onboarding-persona-list onboarding-mesocycle-list">
                    {selectedPersona.periodization.mesocycles.map((block, i) => (
                      <li key={i}>{block}</li>
                    ))}
                  </ul>
                  <p><strong>Microcycles:</strong> {selectedPersona.periodization.microcycles}</p>

                  <h3>Key Differentiator:</h3>
                  <p>{selectedPersona.keyDifferentiator}</p>

                  <h3>Sample Exercises:</h3>
                  <p>{selectedPersona.sampleExercises}</p>
                </div>
              </>
            ) : showingPrograms && selectedPersona ? (
              <div className="onboarding-persona-placeholder">
                <p>Select a program to see details</p>
              </div>
            ) : (
              <div className="onboarding-persona-placeholder">
                <p>Select a path to get started</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main content card (for steps 1, 3) */}
      {currentStep !== 2 && (
      <div className={`onboarding-card ${currentStep === 3 ? 'wide' : ''}`}>
        {/* Step 1: Profile */}
        {currentStep === 1 && (
          <>
            <h2 className="onboarding-card-title">Set up profile</h2>
            <p className="onboarding-privacy-notice">
              This information is used only to personalize your training experience and improve safety. We do not sell your personal data or use it for advertising.
            </p>
            
            <div className="onboarding-form">
              {/* Row 1: Birthdate; Gender */}
              <div className="onboarding-input-row">
                <div className="onboarding-input-wrapper onboarding-input-flex">
                  <div className="onboarding-field-label-wrapper onboarding-age-label-wrapper">
                    <label className="onboarding-field-label">Birthdate</label>
                    <div className="onboarding-info-icon-wrapper onboarding-age-icon-wrapper">
                      <button
                        type="button"
                        className="onboarding-info-icon"
                        onClick={() => setActiveTooltip(activeTooltip === 'birthdate' ? null : 'birthdate')}
                        aria-label="Information about birthdate"
                      >
                        <img src={InfoIcon} alt="Info" />
                      </button>
                      {activeTooltip === 'birthdate' && (
                        <>
                        <div className="onboarding-tooltip-backdrop" onClick={() => setActiveTooltip(null)} />
                        <div className="onboarding-tooltip onboarding-tooltip-age" onClick={(e) => e.stopPropagation()}>
                          <div className="onboarding-tooltip-section">
                            <strong>Why we ask</strong>
                            <p>Your birthdate helps us calculate your age and recommend appropriate training intensity, volume, and recovery guidelines.</p>
                          </div>
                          <div className="onboarding-tooltip-divider">⸻</div>
                          <div className="onboarding-tooltip-section">
                            <strong>How it's used</strong>
                            <p>We use your birthdate only to personalize training recommendations and reduce injury risk. It is never used for medical diagnosis, advertising, or sold to third parties.</p>
                          </div>
                        </div>
                        </>
                      )}
                    </div>
                  </div>
                  <input
                    type="date"
                    placeholder="Birthdate"
                    value={birthdate}
                    onChange={(e) => setBirthdate(e.target.value)}
                    className="onboarding-input"
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div className="onboarding-input-wrapper onboarding-input-flex">
                  <div className="onboarding-field-label-wrapper">
                    <label className="onboarding-field-label">Gender</label>
                    <div className="onboarding-info-icon-wrapper">
                      <button
                        type="button"
                        className="onboarding-info-icon"
                        onClick={() => setActiveTooltip(activeTooltip === 'gender' ? null : 'gender')}
                        aria-label="Information about gender"
                      >
                        <img src={InfoIcon} alt="Info" />
                      </button>
                      {activeTooltip === 'gender' && (
                        <>
                        <div className="onboarding-tooltip-backdrop" onClick={() => setActiveTooltip(null)} />
                        <div className="onboarding-tooltip" onClick={(e) => e.stopPropagation()}>
                          <div className="onboarding-tooltip-section">
                            <strong>Why we ask</strong>
                            <p>Gender helps inform general training patterns such as recovery, volume tolerance, and progression trends.</p>
                          </div>
                          <div className="onboarding-tooltip-divider">⸻</div>
                          <div className="onboarding-tooltip-section">
                            <strong>How it's used</strong>
                            <p>This information is used only to tailor training recommendations. We do not sell this data or use it for advertising. You may choose "prefer not to say."</p>
                          </div>
                        </div>
                        </>
                      )}
                    </div>
                  </div>
                  <select
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    className="onboarding-select"
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="non-binary">Non-binary</option>
                    <option value="prefer_not_to_say">Prefer not to say</option>
                  </select>
                </div>
              </div>

              {/* Row 2: Current Weight/Unit; Goal Weight/Unit */}
              <div className="onboarding-input-row">
                <div className="onboarding-input-wrapper onboarding-input-flex">
                  <div className="onboarding-field-label-wrapper">
                    <label className="onboarding-field-label">Current Weight</label>
                    <div className="onboarding-info-icon-wrapper">
                      <button
                        type="button"
                        className="onboarding-info-icon"
                        onClick={() => setActiveTooltip(activeTooltip === 'weight' ? null : 'weight')}
                        aria-label="Information about weight"
                      >
                        <img src={InfoIcon} alt="Info" />
                      </button>
                      {activeTooltip === 'weight' && (
                        <>
                        <div className="onboarding-tooltip-backdrop" onClick={() => setActiveTooltip(null)} />
                        <div className="onboarding-tooltip" onClick={(e) => e.stopPropagation()}>
                          <div className="onboarding-tooltip-section">
                            <strong>Why we ask</strong>
                            <p>Weight helps us estimate appropriate loading, volume, and progression ranges for training.</p>
                          </div>
                          <div className="onboarding-tooltip-divider">⸻</div>
                          <div className="onboarding-tooltip-section">
                            <strong>How it's used</strong>
                            <p>Your weight is used only to personalize training recommendations and improve safety. It is never shared publicly, sold, or used for advertising.</p>
                          </div>
                        </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="onboarding-weight-input-row">
                    <input
                      type="number"
                      placeholder="Weight"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                      className="onboarding-input"
                      min="50"
                      max="500"
                    />
                    <select
                      value={weightUnit}
                      onChange={(e) => {
                        setWeightUnit(e.target.value);
                        setGoalWeightUnit(e.target.value); // Sync goal weight unit
                      }}
                      className="onboarding-select onboarding-select-small"
                    >
                      <option value="lb">lb</option>
                      <option value="kg">kg</option>
                    </select>
                  </div>
                </div>

                <div className="onboarding-input-wrapper onboarding-input-flex">
                  <div className="onboarding-field-label-wrapper">
                    <label className="onboarding-field-label">Goal Weight</label>
                    <div className="onboarding-info-icon-wrapper">
                      <button
                        type="button"
                        className="onboarding-info-icon"
                        onClick={() => setActiveTooltip(activeTooltip === 'goalWeight' ? null : 'goalWeight')}
                        aria-label="Information about goal weight"
                      >
                        <img src={InfoIcon} alt="Info" />
                      </button>
                      {activeTooltip === 'goalWeight' && (
                        <>
                        <div className="onboarding-tooltip-backdrop" onClick={() => setActiveTooltip(null)} />
                        <div className="onboarding-tooltip" onClick={(e) => e.stopPropagation()}>
                          <div className="onboarding-tooltip-section">
                            <strong>Why we ask</strong>
                            <p>Goal weight helps guide training focus and progression in alignment with your stated goals.</p>
                          </div>
                          <div className="onboarding-tooltip-divider">⸻</div>
                          <div className="onboarding-tooltip-section">
                            <strong>How it's used</strong>
                            <p>Your goal weight is used only to inform training recommendations. It is not used for medical decisions, advertising, or sold to third parties.</p>
                          </div>
                        </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="onboarding-weight-input-row">
                    <input
                      type="number"
                      placeholder="Goal Weight"
                      value={goalWeight}
                      onChange={(e) => setGoalWeight(e.target.value)}
                      className="onboarding-input"
                      min="50"
                      max="500"
                    />
                    <select
                      value={goalWeightUnit}
                      onChange={(e) => setGoalWeightUnit(e.target.value)}
                      className="onboarding-select onboarding-select-small"
                    >
                      <option value="lb">lb</option>
                      <option value="kg">kg</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Row 3: Upload profile pic */}
              <div className="onboarding-input-wrapper">
                <label className="onboarding-field-label">Profile Picture (optional)</label>
                <ProfilePictureCropper
                  currentImageUrl={profilePicturePreview}
                  onImageChange={(file, previewUrl) => {
                    setProfilePicture(file);
                    setProfilePicturePreview(previewUrl);
                  }}
                  shape="square"
                />
              </div>
            </div>
          </>
        )}

        {/* Step 2: Path + Program - handled separately with two-column layout */}
        {currentStep === 2 && null}

        {/* Step 3: Waiver */}
        {currentStep === 3 && (
          <>
            <h2 className="onboarding-card-title">
              LIABILITY WAIVER & ASSUMPTION OF RISK
            </h2>
            
            <div className="onboarding-waiver-container">
              <div className="onboarding-waiver-text">
                <p className="onboarding-waiver-date">
                  Last Updated: January 1, 2026
                </p>
                <p>
                  This Liability Waiver & Assumption of Risk ("Waiver") is entered into between you ("Participant," "you") and Begin Strength and Begin the Work, LLC ("Company," "we," "us").
                </p>
                <p>
                  By accepting this Waiver, you acknowledge that you have read, understood, and voluntarily agree to the following:
                </p>

                <h3>1. Assumption of Risk</h3>
                <p>
                  You understand that participation in physical training, exercise programs, and athletic activity involves inherent risks, including but not limited to muscle strains, sprains, falls, cardiovascular events, illness, injury, permanent disability, or death.
                </p>
                <p>
                  You voluntarily and knowingly assume all risks, both known and unknown, associated with your participation, whether arising from physical exertion, equipment use, environmental conditions, programming decisions, or otherwise.
                </p>

                <h3>2. Medical Clearance & No Medical Advice</h3>
                <p>
                  You acknowledge that the Company does not provide medical advice and is not a medical or healthcare provider.
                </p>
                <p>
                  You agree that it is your sole responsibility to consult with a physician or other qualified healthcare professional before beginning or modifying any exercise or training program, and to determine whether participation is appropriate for your health and physical condition.
                </p>

                <h3>3. Personal Responsibility</h3>
                <p>
                  You acknowledge that you are solely responsible for:
                </p>
                <ul>
                  <li>Your health, safety, and physical condition</li>
                  <li>Proper execution of exercises</li>
                  <li>Modifying or discontinuing activity if you experience pain, dizziness, shortness of breath, or discomfort</li>
                  <li>The safety and suitability of your training environment and equipment</li>
                </ul>
                <p>
                  You understand that training may be provided remotely or asynchronously, and that the Company cannot monitor your form, surroundings, or physical readiness.
                </p>

                <h3>4. Release & Waiver of Liability</h3>
                <p>
                  To the fullest extent permitted by law, you hereby release, waive, and discharge Begin Strength and Begin the Work, LLC, including their owners, officers, employees, contractors, and affiliates, from any and all claims, demands, damages, liabilities, or causes of action, whether arising from ordinary negligence or otherwise, related to your participation in any training, program, workout, or activity.
                </p>
                <p>
                  This release does not apply to gross negligence or willful misconduct, to the extent such liability cannot be waived under Oklahoma law.
                </p>

                <h3>5. Indemnification</h3>
                <p>
                  You agree to indemnify and hold harmless the Company from any claims, losses, damages, liabilities, costs, or expenses (including reasonable attorney's fees) arising out of or related to your participation in training or your breach of this Waiver.
                </p>

                <h3>6. Acknowledgment & Acceptance</h3>
                <p>
                  You acknowledge that:
                </p>
                <ul>
                  <li>You are at least 13 years of age, or have the consent of a parent or legal guardian</li>
                  <li>You fully understand the risks associated with physical training</li>
                  <li>You are voluntarily participating at your own risk</li>
                  <li>You are giving up certain legal rights by accepting this Waiver</li>
                </ul>

                <h3>7. Governing Law</h3>
                <p>
                  This Waiver shall be governed by and construed in accordance with the laws of the State of Oklahoma, without regard to conflict of law principles.
                </p>

                <h3>8. Electronic Acceptance</h3>
                <p>
                  By clicking "I Agree", checking the acceptance box, or otherwise affirmatively indicating your acceptance, you agree that this Waiver is legally binding, equivalent to a handwritten signature.
                </p>
              </div>

              <div className="onboarding-waiver-agreement">
                <label className="onboarding-waiver-checkbox-label">
                  <input
                    type="checkbox"
                    checked={waiverAccepted}
                    onChange={(e) => setWaiverAccepted(e.target.checked)}
                    className="onboarding-waiver-checkbox"
                  />
                  <span>I have read and understand the Liability Waiver, acknowledge the risks of exercise, and voluntarily agree to participate at my own risk.</span>
                </label>
              </div>

              <div className="onboarding-waiver-actions">
                <button
                  className="onboarding-waiver-cancel"
                  onClick={() => navigate('/')}
                >
                  Cancel & Exit
                </button>
                <button
                  onClick={goNext}
                  className="onboarding-waiver-finish"
                  disabled={!waiverAccepted || loading}
                >
                  {loading ? 'Please wait...' : 'Finish'}
                </button>
              </div>
            </div>
          </>
        )}

        {/* Error message */}
        {error && <p className="onboarding-error">{error}</p>}

        {/* Next/Finish button (for step 1, Finish button is in Step 3) */}
        {currentStep !== 3 && currentStep !== 2 && (
          <button
            onClick={goNext}
            className="c-ripple onboarding-submit"
            disabled={!isCurrentStepValid() || loading}
          >
            {loading ? 'Please wait...' : 'Next'}
          </button>
        )}
      </div>
      )}
    </div>
  );
}

export default OnboardingFlow;

